import { doc, collection, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getBytes, deleteObject } from 'firebase/storage'
import { db, storage } from '@/firebase'
import {
  RULE_EXPECTATIONS,
  attempt,
  accessDetail,
  fmtExpected,
  probe
} from '../ruleExpectations.js'

// Live document (Storage) access checks for the current role: creates a tagged
// smoke student on YOUR caseload (caseManagerId names you), then probes
// upload/download/delete against the same storage.rules paths real student
// documents use — the general folder and the sensitive folder (IEP/BIP-tier).
//
// Storage writes use the role and case-manager relationship. Reads use that
// relationship or a full-read role; no client-seeded staffIds grant is needed.
//
// Role-aware: if the role cannot create students, the create denial is asserted
// as a pass and the storage probes are skipped.
export default {
  id: 'documentsAccess',
  label: 'Student document storage access',
  description: 'Upload/read/delete probes on general and sensitive document folders for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const studentRef = doc(collection(db, 'students'))
    const payload = new Blob([`smoke test ${ctx.runId}`], { type: 'text/plain' })
    const generalRef = storageRef(storage, `students/${studentRef.id}/general/smoke-probe.txt`)
    const sensitiveRef = storageRef(storage, `students/${studentRef.id}/sensitive/smoke-probe.txt`)
    const expectCreate = RULE_EXPECTATIONS.studentCreate(role)
    let created = false

    const docFacts = { isCaseManagerOfStudent: true, inStaffIds: false }

    try {
      await step(`create smoke student (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(studentRef, {
            app: {
              studentData: {
                firstName: 'SMOKE',
                lastName: 'DocAccess',
                plan: 'IEP',
                caseManagerId: ctx.user.uid
              },
              accommodations: { instruction: '', assessment: '' },
              schedule: { classServices: [] },
              staffIds: []
            },
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — students/${studentRef.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!created) {
        step.skip('storage probes', 'Role cannot create — later steps skipped')
        return
      }

      const expectGeneral = RULE_EXPECTATIONS.storageStudentWrite(role, docFacts)
      await step(`upload to general folder (expected: ${fmtExpected(expectGeneral)})`, () =>
        probe(expectGeneral, () => uploadBytes(generalRef, payload), {
          onUnexpectedSuccess: () => deleteObject(generalRef).catch(() => {})
        })
      )

      if (expectGeneral) {
        await step('download from general folder', async () => {
          const bytes = await getBytes(generalRef)
          if (!bytes.byteLength) throw new Error('Downloaded file is empty')
          return `${bytes.byteLength} bytes read back`
        })
      }

      const expectSensitive = RULE_EXPECTATIONS.storageStudentWrite(role, docFacts)
      await step(`upload to sensitive folder (expected: ${fmtExpected(expectSensitive)})`, () =>
        probe(expectSensitive, () => uploadBytes(sensitiveRef, payload), {
          onUnexpectedSuccess: () => deleteObject(sensitiveRef).catch(() => {})
        })
      )

      if (expectSensitive) {
        await step('download from sensitive folder', async () => {
          const bytes = await getBytes(sensitiveRef)
          if (!bytes.byteLength) throw new Error('Downloaded file is empty')
          return `${bytes.byteLength} bytes read back`
        })
      }
    } finally {
      await deleteObject(generalRef).catch(() => {})
      await deleteObject(sensitiveRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
